"use client";

import Link from "next/link";
import { Fragment, useMemo, useState } from "react";
import type { InternetTableRow } from "@/lib/ur-internet";

function ServiceLines({ row }: { row: InternetTableRow }) {
  return (
    <>
      {row.lines.map((group, groupIndex) => (
        <Fragment key={group.method}>
          {groupIndex > 0 && " ／ "}
          {group.prefixed && `${group.method}：`}
          {group.links.map((link, linkIndex) => (
            <Fragment key={`${link.label}-${linkIndex}`}>
              {linkIndex > 0 && "、"}
              <a href={`#${link.anchor}`} className="text-accent hover:underline">
                {link.label}
              </a>
            </Fragment>
          ))}
        </Fragment>
      ))}
      {row.note && (row.lines.length > 0 ? `。${row.note}` : row.note)}
    </>
  );
}

export function UrInternetTableClient({
  rows,
  asOfLabel,
  sourcePage,
}: {
  rows: InternetTableRow[];
  asOfLabel: string;
  sourcePage: string;
}) {
  const [prefecture, setPrefecture] = useState("all");
  const [query, setQuery] = useState("");

  const prefectures = useMemo(() => {
    const seen = new Set<string>();
    const list: string[] = [];
    for (const row of rows) {
      if (seen.has(row.prefecture)) continue;
      seen.add(row.prefecture);
      list.push(row.prefecture);
    }
    return list;
  }, [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().normalize("NFKC");
    return rows.filter((row) => {
      if (prefecture !== "all" && row.prefecture !== prefecture) return false;
      if (!q) return true;
      return row.name.normalize("NFKC").includes(q) || row.city.normalize("NFKC").includes(q);
    });
  }, [rows, prefecture, query]);

  return (
    <div className="not-prose my-6">
      <div className="flex flex-col sm:flex-row gap-3 mb-3">
        <label className="text-sm text-muted flex flex-col gap-1">
          都道府県
          <select
            value={prefecture}
            onChange={(event) => setPrefecture(event.target.value)}
            className="border border-border rounded-lg px-3 py-2 text-foreground bg-white"
          >
            <option value="all">すべて</option>
            {prefectures.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-muted flex flex-col gap-1 flex-1">
          団地名・所在地
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="例：高島平、浦安市"
            className="border border-border rounded-lg px-3 py-2 text-foreground"
          />
        </label>
      </div>
      <p className="text-sm text-muted mb-2">{filtered.length}件</p>
      <div className="overflow-auto max-h-[32rem] border border-border rounded-lg">
        <table className="w-full text-sm">
          <thead className="sticky top-0">
            <tr className="bg-blue-50 text-left">
              <th className="px-3 py-2 font-semibold whitespace-nowrap">都道府県</th>
              <th className="px-3 py-2 font-semibold whitespace-nowrap">所在地</th>
              <th className="px-3 py-2 font-semibold whitespace-nowrap">団地名</th>
              <th className="px-3 py-2 font-semibold">使えるインターネット回線</th>
              <th className="px-3 py-2 font-semibold whitespace-nowrap">団地ページ</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <tr key={row.id} className="border-t border-border even:bg-gray-50/50">
                <td className="px-3 py-2 whitespace-nowrap">{row.prefecture}</td>
                <td className="px-3 py-2 whitespace-nowrap">{row.city}</td>
                <td className="px-3 py-2">{row.name}</td>
                <td className="px-3 py-2">
                  <ServiceLines row={row} />
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {row.href ? (
                    <Link href={row.href} className="text-accent hover:underline">
                      団地ページ
                    </Link>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-muted">
                  条件に合う団地が見つかりません
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted mt-2">
        出典：
        <a href={sourcePage} className="text-accent hover:underline" target="_blank" rel="noopener noreferrer">
          UR都市機構 団地別利用可能方式一覧
        </a>
        （{asOfLabel}）
      </p>
    </div>
  );
}
